import React from 'react';
import styles from './ParkingCard.module.css';
import Icon from '../../Icon';
import CardIconButton from '../../UI/CardIconButton/CardIconButton';
import { useFavorite } from '../../../pages/FavoritePage/FavoriteContext';
import { useCompare } from '../../../pages/ComparePage/CompareContext';
import { ParkingCardData } from '../types';
import { useNavigate } from 'react-router-dom';
import { PARKING_CARD_PAGE } from '../../../router/paths';
import { calculateDiscountedPrice, formatPrice } from '../../../utils/discountPriceUtils';
import cn from 'classnames';

export interface ParkingCardProps {
  parkingData: ParkingCardData;
  isDiscounted: boolean;
}

export default function ParkingCard({ parkingData, isDiscounted }: ParkingCardProps) {
  const { favorites, toggleFavorite } = useFavorite();
  const { compares, toggleCompare } = useCompare();
  const navigate = useNavigate();

  const isFavorite = favorites.some((item) => item.id === parkingData.id && item.type === 'parking');
  const isCompared = compares.some((item) => item.id === parkingData.id && item.type === 'parking');

  const handleToggleFavorite = () => {
    toggleFavorite({
      ...parkingData,
      type: 'parking',
      roomType: '',
      entrance: 0,
      image: '',
      imageSecond: '',
      specialOptions: [],
      windowOptions: [],
      projectType: '',
      cellingHeight: 0,
      checkIn: '',
      totalEntrances: 0,
      livingArea: '',
    });
  };

  const handleToggleCompare = () => {
    toggleCompare({
      ...parkingData,
      type: 'parking',
      roomType: '',
      entrance: 0,
      image: '',
      imageSecond: '',
      specialOptions: [],
      windowOptions: [],
      projectType: '',
      cellingHeight: 0,
      checkIn: '',
      totalEntrances: 0,
      livingArea: '',
    });
  };

  const handleCardClick = () => {
    navigate(PARKING_CARD_PAGE.replace(':id', parkingData.id.toString()));
  };

  const getFloorLabel = (floor: number): string => {
    if (floor === 0) return 'Цоколь';
    return `${floor} этаж`;
  };

  const computedTotalPrice =
    parkingData.price && parkingData.discountProperty
      ? calculateDiscountedPrice(parkingData.pricePerM2, parkingData.discountProperty, parkingData.price)
      : parkingData.price;

  return (
    <div className={styles.wrapper} onClick={handleCardClick}>
      <div className={styles.info}>
        <div className={styles.top}>
          <div className={styles.room}>
            <div className={styles.room_number}>
              <h4>Машино-место</h4>
            </div>
            <div className={styles.room_m2}>
              <h4>
                <span>{parkingData.area}</span> м²
              </h4>
            </div>
          </div>
        </div>
        <div className={cn('text_body_b3', styles.additional_info)}>
          <div>
            <p>
              №<span>{parkingData.numberPlace}</span>
            </p>
          </div>
          <div className={styles.additional_info_separator}></div>
          <div>
            <p>
              <span>{getFloorLabel(parkingData.floor)}</span>
            </p>
          </div>
        </div>
      </div>
      <div className={styles.price_block}>
        {isDiscounted && (
          <div className={styles.discount}>
            <div className={styles.discount_icon}>
              <Icon name='hotSale' width='13' height='16' />
            </div>
            <div className={styles.discount_title}>
              <p className='text_subheaders_sh3'>Выгодное предложение</p>
            </div>
          </div>
        )}
        <div className={styles.price_group}>
          <div className={styles.price_area}>
            <div className={styles.price}>
              <h4>
                <span>{formatPrice(computedTotalPrice)}</span> ₽
              </h4>
            </div>
            {isDiscounted && parkingData.price && (
              <div className={styles.price_no_discount}>
                <p className="text_body_b2">
                  <span>{formatPrice(parkingData.price)}</span> ₽
                </p>
              </div>
            )}
          </div>
          <div className={styles.buttons}>
            <div className={styles.buttons}>
              <CardIconButton
                iconType='icon2'
                isActive={isFavorite}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleToggleFavorite();
                }}
              />
              <CardIconButton
                iconType='icon3'
                isActive={isCompared}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleToggleCompare();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
